import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {Mode} from './config.service';

/**
 * Beschreibt die Favicon-Dateien eines Portals.
 *
 * `svg` und `svgDark` sind optional: Portale ohne SVG-Favicon (aktuell Just
 * Farming) liefern nur das `.ico` aus; Portale ohne eigene Dark-Variante nur
 * das Light-SVG, das dann in beiden Farbschemata verwendet wird.
 */
interface FaviconSet {
  svg?: string;
  svgDark?: string;
  ico: string;
}

/**
 * Setzt die Favicon-`<link>`-Tags portalabhängig nach dem aktiven Modus (SHEL-62).
 *
 * Hintergrund und Begründung: Die `index.html` enthält bewusst KEIN statisches
 * Favicon mehr und es wird auch keine Root-`/favicon.ico` ausgeliefert
 * (aus den Assets in `angular.json` entfernt). Dadurch erscheint vor dem Laden
 * der Konfiguration KEIN falsches Portal-Branding im Browser-Tab - der Browser
 * zeigt übergangsweise sein generisches Standard-Icon. Erst wenn der Portal-Modus
 * feststeht, baut dieser Service die passenden `<link rel="icon">`-Tags auf
 * (Entscheidung in SHEL-62: lieber kurz kein Icon als kurz das falsche).
 */
@Injectable()
export class FaviconService {

  /**
   * Pro Modus die auszuliefernden Favicon-Dateien.
   *
   * Gewerbeportal: Light- und Dark-Variante als SVG (Browser wählt über das
   * `media`-Attribut selbst) plus `.ico` als Raster-Fallback für Browser ohne
   * SVG-Favicon-Unterstützung (z.B. ältere Safari-Versionen).
   * Just Farming ('default'): bewusst NUR das bestehende `.ico`, damit sich das
   * gewohnte Just-Farming-Tab-Icon optisch nicht ändert.
   */
  private static readonly FAVICONS: Record<Mode, FaviconSet> = {
    'default': {
      ico: 'assets/favicons/default/favicon.ico',
    },
    'gewerbeportal': {
      svg: 'assets/favicons/gewerbeportal/favicon-light.svg',
      svgDark: 'assets/favicons/gewerbeportal/favicon-dark.svg',
      ico: 'assets/favicons/gewerbeportal/favicon.ico',
    },
  };

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
  }

  /**
   * Baut die Favicon-Links für den übergebenen Modus neu auf.
   *
   * Idempotent: Vorhandene `link[rel~="icon"]`-Tags werden zuerst entfernt und
   * anschließend neu erzeugt. Das verhindert Doubletten, wenn der Modus zur
   * Laufzeit wechselt (z.B. über den `?shellMode`-Test-Override).
   *
   * Unbekannte Modi fallen auf den 'default'-Satz zurück, damit neue Portale
   * ohne eigenes Favicon nicht dauerhaft ohne Icon bleiben.
   */
  setForMode(mode: Mode): void {
    const favicon = FaviconService.FAVICONS[mode] ?? FaviconService.FAVICONS['default'];
    const head = this.document.head;

    head.querySelectorAll('link[rel~="icon"]').forEach(element => element.remove());

    // INFO: Die Dark-Variante zuerst einhängen - sie greift nur, wenn der
    // Browser 'prefers-color-scheme: dark' meldet; sonst gilt das Light-SVG.
    if (favicon.svgDark) {
      this.appendIconLink('image/svg+xml', favicon.svgDark, '(prefers-color-scheme: dark)');
    }
    if (favicon.svg) {
      this.appendIconLink('image/svg+xml', favicon.svg);
    }
    // INFO: Raster-Fallback zuletzt - Browser mit SVG-Unterstützung bevorzugen
    // die SVG-Einträge, ältere Browser nutzen das `.ico`.
    this.appendIconLink('image/x-icon', favicon.ico);
  }

  private appendIconLink(type: string, href: string, media?: string): void {
    const link = this.document.createElement('link');
    link.rel = 'icon';
    link.type = type;
    link.href = href;
    if (media) {
      link.media = media;
    }
    this.document.head.appendChild(link);
  }
}
